import { ZION_DLQ_STREAM } from "./dlq-message.js";
//#region src/platform-streams.ts
/**
* The two platform registries a NATS grant may name (2026-10-05-stream-resource-permissions, overview
* D-3, D-14 and D-23):
*
* - {@link PLATFORM_STREAMS}: the plain JetStream streams on a data plane that no single principal
*   owns. A `nats_stream` resource id is a principal slug (`machine_principals.agent_id`, whose stream
*   is `S_<org>_<slug>`) or one of these names. Every verb on them is reserved: no grant row reaches
*   them, and only the standing grants and the literal system lists of core-api do.
* - {@link NATS_KV_BUCKETS}: the JetStream KV buckets of the platform, keyed by bucket name. A
*   `nats_kv_bucket` resource id is one of these names. The bucket `B` lives in the stream `KV_B`.
*
* Producers and consumers:
* - `@zion/core-api` reads both registries in `ResourceLocator` (a registered name is a resource), in
*   the grant route (`isNatsGrantVerbReserved` answers 400 `verb_reserved`) and in the credential
*   compile, through `compileNatsGrantSubjects` in `stream-verbs.ts`.
* - `@zion/core-api`'s enroll refuses an `agentId` that `AGENT_ID_RESERVED_PATTERN` (in
*   `machine-principal.ts`) matches, so no principal slug can equal a platform stream name.
* - `nats-standing-grants.ts` names the buckets of its rows by these names.
*
* Load order. This module imports one value, `ZION_DLQ_STREAM` from `dlq-message.ts`, which imports
* nothing, and types only from `stream-verbs.ts`. `event-subjects.ts`, `stream-verbs.ts` and
* `nats-standing-grants.ts` import each other and read these registries. If this module read a
* constant of `event-subjects.ts` while it loads, the first importer of `event-subjects.ts` would meet
* an uninitialised binding and throw a `ReferenceError` (ruling R-003). So the names of `ZION_AUDIT`,
* `ZION_SCALING` and the five buckets are literals here, and `platform-streams.test.ts` pins each one
* to its source symbol: `SCALING_STREAM_NAME`, `COORDINATION_KV_BUCKETS` and
* `PLATFORM_AUDIT_STREAM_NAME` of `@zion/sdk-cp-dp-contracts`.
*
* The registries state what the platform declares. They do not read the live broker. Every export is
* pure and frozen.
*/
/**
* The grammar of every platform stream name: `ZION_`, then one or more of `A-Z`, `a-z`, `0-9`, `_`
* and `-`, with 255 characters at most in all. The character class and the bound are the stream-name
* grammar of `StreamSpecSchema` (`stream-spec.ts`), so every name that matches is a legal NATS stream
* name and a single subject token.
*
* A registered name matches it. A string that matches it is not always registered: `ZION_<APP>` app
* streams match and are no platform streams. {@link isPlatformStreamName} answers registration.
*/
const PLATFORM_STREAM_NAME_PATTERN = /^(?=.{1,255}$)ZION_[A-Za-z0-9_-]+$/;
/** A frozen registry row, with its verb list frozen too. */
function platformStream(entry) {
	return Object.freeze({
		...entry,
		orgGrantableVerbs: Object.freeze([...entry.orgGrantableVerbs])
	});
}
/**
* Every platform stream a `nats_stream` resource id may name, in a fixed order.
*
* | name | org-grantable verbs |
* |---|---|
* | `ZION_AUDIT` | none |
* | `ZION_DLQ` | none |
* | `ZION_SCALING` | none |
*
* `ZION_CORE-API` and `ZION_SATELLITE` have no author in the tree, so the registry leaves them out
* (overview D-3). The KV streams `KV_<bucket>` are no `nats_stream` ids: their buckets are
* `nats_kv_bucket` resources in {@link NATS_KV_BUCKETS} (overview D-23). The array and every row are
* frozen.
*/
const PLATFORM_STREAMS = Object.freeze([
	platformStream({
		name: "ZION_AUDIT",
		orgGrantableVerbs: []
	}),
	platformStream({
		name: ZION_DLQ_STREAM,
		orgGrantableVerbs: []
	}),
	platformStream({
		name: "ZION_SCALING",
		orgGrantableVerbs: []
	})
]);
/**
* The registry row whose `name` equals `name` exactly, or `undefined`.
*
* Callers: core-api's `ResourceLocator` and grant route, which turn a resource id into a platform
* stream target; the credential compile; `stream-verbs.ts`, which checks that an `org-grant`
* target is a registry row. The match is exact and case-sensitive, as NATS stream names are. Any
* other input, a non-string included, answers `undefined`. Never throws.
*/
function platformStreamOf(name) {
	return PLATFORM_STREAMS.find((stream) => stream.name === name);
}
/**
* True when `id` is the name of a registered platform stream (see {@link platformStreamOf}). A name
* that matches {@link PLATFORM_STREAM_NAME_PATTERN} and is not registered answers `false`, and so does
* a non-string. Never throws.
*/
function isPlatformStreamName(id) {
	return platformStreamOf(id) !== void 0;
}
/**
* The key layouts of a KV bucket.
*
* - `org-prefixed`: every key of the bucket starts with `<orgId>.`, so a subject on
*   `$KV.<bucket>.<orgId>.>` confines a grant to one org's keys.
* - `none`: the keys carry no org token. No subject can confine a grant on the bucket to one org.
*
* Frozen.
*/
const NATS_KV_BUCKET_KEY_LAYOUTS = Object.freeze(["org-prefixed", "none"]);
/**
* The grammar of a KV bucket name: 1 to 252 of `A-Z`, `a-z`, `0-9`, `_` and `-`. The bound keeps the
* stream name `KV_<bucket>` inside the 255 characters of a NATS stream name, and the class keeps the
* name one subject token. Every registered name matches it.
*/
const NATS_KV_BUCKET_NAME_PATTERN = /^[A-Za-z0-9_-]{1,252}$/;
/** A frozen bucket row, with its verb list frozen too. */
function kvBucket(entry) {
	return Object.freeze({
		...entry,
		orgGrantableVerbs: Object.freeze([...entry.orgGrantableVerbs])
	});
}
/** The bucket rows by name, in the order of `COORDINATION_KV_BUCKET_NAMES`. */
const KV_BUCKET_ROWS = {
	AGENT_LEASES: kvBucket({
		name: "AGENT_LEASES",
		keyLayout: "org-prefixed",
		orgGrantableVerbs: ["manage", "read"]
	}),
	"event-server-leader": kvBucket({
		name: "event-server-leader",
		keyLayout: "none",
		orgGrantableVerbs: []
	}),
	"s3-archiver-leader": kvBucket({
		name: "s3-archiver-leader",
		keyLayout: "none",
		orgGrantableVerbs: []
	}),
	AGENT_REVOCATIONS: kvBucket({
		name: "AGENT_REVOCATIONS",
		keyLayout: "org-prefixed",
		orgGrantableVerbs: ["manage", "read"]
	}),
	"audit-archiver-leader": kvBucket({
		name: "audit-archiver-leader",
		keyLayout: "none",
		orgGrantableVerbs: []
	})
};
/**
* Every KV bucket a `nats_kv_bucket` resource id may name, keyed by bucket name, in the order of
* `COORDINATION_KV_BUCKET_NAMES`. Each row's `name` equals its key.
*
* | bucket | key layout | org-grantable verbs |
* |---|---|---|
* | `AGENT_LEASES` | org-prefixed (`<org>.<principalId>`) | `manage`, `read` |
* | `event-server-leader` | none | none |
* | `s3-archiver-leader` | none | none |
* | `AGENT_REVOCATIONS` | org-prefixed (`<orgId>.<principalId>`) | `manage`, `read` |
* | `audit-archiver-leader` | none (the one key `leader`) | none |
*
* `write` is reserved on every bucket: a write on a leader bucket seizes leadership, a write on
* `AGENT_REVOCATIONS` removes or forges a revocation, and a write on `AGENT_LEASES` replaces a lease
* value whose revision is another agent's fencing epoch (index WS-6 item 3). A widening of a row's
* `orgGrantableVerbs` is a security ruling. The record has a null prototype and is frozen at every
* level, so a read with an untrusted key such as `constructor` answers `undefined`; a caller that
* holds an untrusted key uses {@link natsKvBucketOf}.
*/
const NATS_KV_BUCKETS = Object.freeze(Object.assign(Object.create(null), KV_BUCKET_ROWS));
/**
* The bucket row whose name equals `name` exactly, or `undefined`.
*
* Callers: core-api's `ResourceLocator`, grant route and credential compile, which turn a
* `nats_kv_bucket` resource id into a bucket target; `stream-verbs.ts`, which checks that an
* `org-grant` target is a registry row. The match is exact and case-sensitive. Any other input, a
* non-string or an inherited member name included, answers `undefined`. Never throws.
*/
function natsKvBucketOf(name) {
	if (typeof name !== "string" || !Object.hasOwn(NATS_KV_BUCKETS, name)) return void 0;
	return NATS_KV_BUCKETS[name];
}
/**
* True when `id` is the name of a registered KV bucket (see {@link natsKvBucketOf}). Any other input
* answers `false`. Never throws.
*/
function isNatsKvBucketName(id) {
	return natsKvBucketOf(id) !== void 0;
}
//#endregion
export { NATS_KV_BUCKETS, NATS_KV_BUCKET_KEY_LAYOUTS, NATS_KV_BUCKET_NAME_PATTERN, PLATFORM_STREAMS, PLATFORM_STREAM_NAME_PATTERN, isNatsKvBucketName, isPlatformStreamName, natsKvBucketOf, platformStreamOf };

//# sourceMappingURL=platform-streams.js.map